import React, { Fragment, useState, useEffect } from "react";
import { backend, API_URL } from "../../../config";
import {
  Button,
  Typography,
  Modal,
  CircularProgress,
  Container,
  Grid,
  TextField,
  InputLabel,
  Autocomplete,
} from "@mui/material";

import DatePicker from "@mui/lab/DatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";

import ManagementNav from "../../../features/OrderManagement/components/Nav/ManagementNav";

import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
  },
  layout: {
    width: "auto",
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(600)]: {
      width: 900,
      marginLeft: "auto",
      marginRight: "auto",
    },
  },
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(5),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
  stepper: {
    padding: theme.spacing(3, 0, 5),
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-end",
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
  modal: {
    position: "absolute",
    width: 400,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: ".3rem",
    boxShadow: theme.shadows[5],
    padding: "1rem",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
}));

export const FinalizeInvoices = (props) => {
  const classes = useStyles();

  const cDate = new Date();

  const [confirmModal, setConfirmModal] = useState(false);
  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(cDate);
  const [orders, setOrders] = useState([]);
  const [ordersSelected, setOrdersSelected] = useState([]);
  const [ordersFinalized, setOrdersFinalized] = useState([]);

  const ordersFinalizedIds = ordersFinalized.map((o) => +o.split("#")[1]);

  const handleChangeOrders = (e, value) => {
    setOrdersSelected(value);
  };

  const handleChangeDate = (e) => {
    const { value } = e.target;
    setOrders([]);
    setOrdersSelected([]);
    setDeliveryDate(value);
  };

  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const confirmModalContent = (
    <div className={classes.modal}>
      {!loadingFlag && !finishedFlag ? (
        <>
          Are you sure you want to bulk finalize invoices?
          <br />
          <br />
          <div align="right">
            <Button
              onClick={() => toggleConfirmModal()}
              className={classes.button}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendRequest()}
              className={classes.button}
            >
              Confirm
            </Button>
          </div>
        </>
      ) : loadingFlag ? (
        <>
          <div align="center">
            <CircularProgress />
            <div>
              Generating Invoices <br />
              This may take some time relative to the number of invoices
            </div>
          </div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              There was an error while generating invoices
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "scroll",
                  display: "inline-block",
                  width: "100%",
                }}
              >
                <b>Error:</b>
                <br />
                <div dangerouslySetInnerHTML={{ __html: errorInfo.message }} />
              </span>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => resolveRequest()}
                  className={classes.button}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <>
          The invoices were finalized successfully.
          {errorInfo.error ? (
            <>
              <br />
              <br />
              There was an error while finalizing invoices
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "scroll",
                  display: "inline-block",
                  width: "100%",
                }}
              >
                <b>Error:</b>
                <br />
                <div dangerouslySetInnerHTML={{ __html: errorInfo.message }} />
              </span>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => resolveRequest()}
                  className={classes.button}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
          <div align="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => resolveRequest()}
              className={classes.button}
            >
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const resolveRequest = () => {
    setLoadingFlag(false);
    setFinishedFlag(false);
    toggleConfirmModal();
    setErrorInfo({ error: false, message: "" });
  };

  const sendRequest = async () => {
    setLoadingFlag(true);

    Promise.all(
      ordersSelected.map(
        async (orderObj) =>
          await backend
            .post(`${API_URL}orders/${orderObj.id}/invoice/codpaymentfinalize`)
            .then(({ data }) => data)
            .catch((error) => {
              console.log(error);
              const errorData = error.response.data;

              setErrorInfo((prevState) => {
                let newMsg =
                  typeof errorData === "object"
                    ? errorData.hasOwnProperty("message")
                      ? errorData.message
                      : errorData.detail
                    : error.message;

                if (newMsg === undefined) {
                  const orderId = orderObj.id;

                  if (!prevState.message.includes(orderId)) {
                    newMsg = `Invoice ${orderId}: ${error.response.statusText}`;
                  } else {
                    newMsg = "";
                  }
                }

                if (newMsg !== "") {
                  const errorMsg =
                    prevState.message +
                    `${prevState.message === "" ? "" : "<br />"}` +
                    newMsg;

                  return {
                    error: true,
                    message: errorMsg,
                  };
                }

                return prevState;
              });
            }),
      ),
    ).then(async (result) => {
      setLoadingFlag(false);
      setFinishedFlag(true);
    });
  };

  useEffect(() => {
    const getOrders = async () => {
      let deliveryDateObj = new Date(deliveryDate);

      const deliveryDateParam = new Date(
        deliveryDateObj.getTime() - deliveryDateObj.getTimezoneOffset() * 60000,
      )
        .toISOString()
        .split("T")[0];

      await backend
        .get(
          `${API_URL}orders?no_page=1&start_date=${deliveryDateParam}&end_date=${deliveryDateParam}`,
        )
        .then(({ data }) => {
          if (data.length) {
            let oSelected = [];
            let oInvoiced = [];

            const dataFiltered = data.filter(
              (item) =>
                item.invoiced && item?.company_location?.cod_payments_enabled,
            );

            dataFiltered.forEach((item) => {
              if (["c", "x"].includes(item.paid_status)) {
                oInvoiced.push(
                  `${item.customer ? item.customer.username : "Anonymous"} #${
                    item.id
                  }`,
                );
              } else {
                oSelected.push(item);
              }
            });

            setOrders(dataFiltered);
            setOrdersFinalized(oInvoiced);
            setOrdersSelected(oSelected);
          }
        });
    };

    if (deliveryDate !== null) {
      getOrders();
    }
  }, [deliveryDate]);

  return (
    <Container style={{ minHeight: "80vh" }}>
      <Modal open={confirmModal} onClose={toggleConfirmModal}>
        {confirmModalContent}
      </Modal>
      <ManagementNav {...props} />
      <Typography component="h6" variant="h6" align="center">
        Bulk Finalize Invoices
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          Select the invoices you would like to finalize, this action will lock
          the invoice from further changes
        </Grid>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          Load invoices from a given date.
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="MM/dd/yyyy"
              margin="normal"
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
              value={deliveryDate}
              renderInput={(props) => (
                <TextField
                  {...props}
                  fullWidth
                  label="Delivery date"
                  variant="standard"
                />
              )}
              onChange={(date) =>
                handleChangeDate({
                  target: {
                    name: "delivery_date",
                    value: date,
                  },
                })
              }
            />
          </LocalizationProvider>
        </Grid>
        {deliveryDate !== null ? (
          <>
            <Grid item xs={12} sm={12}>
              <InputLabel shrink id="orders">
                Invoices
              </InputLabel>
              <Autocomplete
                fullWidth
                multiple
                disablePortal
                size="small"
                name="orders"
                options={orders}
                getOptionLabel={(option) =>
                  `${
                    option.customer ? option.customer.username : "Anonymous"
                  } #${option.id}`
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" variant="standard" />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    {`${
                      option.customer ? option.customer.username : "Anonymous"
                    } #${option.id}`}
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionDisabled={(option) =>
                  ordersFinalizedIds.includes(option.id)
                }
                onChange={(e, value) => handleChangeOrders(e, value)}
                helpertext="Select the orders to include in the receipt difference."
                value={ordersSelected}
              />
            </Grid>
            {ordersFinalized.length > 0 && (
              <Grid item xs={12}>
                <b>
                  There are {ordersFinalized.length} invoices finalized already:
                </b>
                {` ${ordersFinalized.join(", ")}`}
              </Grid>
            )}
          </>
        ) : (
          <></>
        )}
        <Grid item xs={12} sm={12} style={{ textAlign: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => toggleConfirmModal()}
            className={classes.button}
            disabled={!ordersSelected.length || loadingFlag}
          >
            Finalize Invoices
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default FinalizeInvoices;
